import { Badge, Icon, type IconName } from '@nexa/ui';

const TINT = {
  brand: 'bg-brand-50 text-brand-600',
  success: 'bg-success-bg text-success-fg',
  violet: 'bg-violet-bg text-violet-fg',
} as const;

export interface ModuleCardProps {
  readonly icon: IconName;
  readonly tint: keyof typeof TINT;
  readonly title: string;
  readonly body: string;
  readonly beta?: boolean;
}

/**
 * Một card trong grid hairline của `#modules` (`template.html` dòng 797–809).
 *
 * BA tint, không phải một: thiết kế dùng brand cho 9 card, success cho Voice, violet cho hai card
 * BETA. Đếm lại toàn file ngày 2026-08-19 chứ không chép tài liệu — `reference.md` §3 "icon tile"
 * chỉ ghi cặp brand, đã báo chủ skill. Đừng "sửa" bảng TINT cho khớp tài liệu.
 *
 * `tone="beta"` (chữ trần), KHÔNG phải `beta-pill`: thiết kế có HAI hình dạng BETA và bản có nền
 * pill thuộc `#knowledge` ở lô C (`template.html` dòng 963). Dùng sai tone ở đây thì mọi cổng vẫn
 * XANH — cả hai đều là class hợp lệ trỏ vào token có thật, và `check-classes` đã được đo là không
 * bắt được loại sai này.
 *
 * KHÔNG có `border` trên card: đường kẻ do `gap-px` của grid cha lộ nền ra tạo thành. Thêm border
 * ở đây là ra đường kẻ ĐÔI ở chỗ giáp nhau (`reference.md` §3 "Grid card hairline").
 *
 * `h-full` là bắt buộc, không phải trang trí: phần tử con của grid là `Reveal` chứ không phải card
 * này, và `Reveal` được kéo giãn hết chiều cao ô còn card thì cao theo nội dung. Thiếu `h-full`
 * thì card nào có mô tả ngắn sẽ để hở đáy ô, và nền kẻ `ink-100` của grid cha lộ ra thành một
 * vệt xám — chỉ thấy khi các card lệch chiều cao, tức gần như mọi khổ màn hình. jsdom không chạy
 * layout engine nên KHÔNG test nào bắt được.
 *
 * `h3` chứ không `div`: 12 card là 12 mục con của một section có `h2`, nên đây là phân cấp thật.
 * Cổng `check:seo` đòi đúng 1 `h1` mỗi trang và `h1` đó thuộc hero — đừng nâng bậc ở đây.
 */
export function ModuleCard({ icon, tint, title, body, beta }: ModuleCardProps) {
  return (
    <div className="nx-tile-host h-full bg-surface px-6 py-[26px] transition [transition-duration:var(--duration-hover)] hover:bg-surface-muted">
      <div className={`nx-tile-lift grid size-9 place-items-center rounded-tile ${TINT[tint]}`}>
        <Icon name={icon} size={18} />
      </div>

      <h3 className="mt-4 text-title-sm font-bold text-ink-900">
        {title}
        {beta === true && (
          <Badge tone="beta" className="ml-1.5">
            BETA
          </Badge>
        )}
      </h3>

      <p className="mt-[7px] text-body-sm text-ink-600">{body}</p>
    </div>
  );
}
